import { GridPaginationModel } from '@mui/x-data-grid'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import {
  CreditCardDisplayStatus,
  CreditCardListModel,
  CreditCardType,
} from 'src/models/creditCard.models'
import {
  CreditCardsDataModel,
  useQueryCreditCardsPaginated,
} from 'src/queries/creditCard.queries'
import { QueryCacheOptions } from 'src/queries/queries.constants'
import {
  useQueryCreditCardBudgetsStatistics,
  useQueryCreditCardVCNStatistics,
} from 'src/queries/statistics/statistics.queries'
import { StatisticModelType } from 'src/requests/statistics.request'

export interface ExtendedCreditCardListModel extends CreditCardListModel {
  numBudgets?: number
  numVcns?: number
}

interface UseCreditCardsWithStatsProps {
  filters: {
    search: string
    displayStatuses: CreditCardDisplayStatus[]
  }
  queryCacheOptions?: QueryCacheOptions
  paginationModel: GridPaginationModel
  userId?: string | string[]
}

type UseCreditCardsWithStatsHook =
  | {
      creditCardsData: CreditCardsDataModel
      isCreditCardsLoading: false
      isSuccess: true
      isError: false
      creditCardsRows: ExtendedCreditCardListModel[]
    }
  | {
      creditCardsData: null
      isCreditCardsLoading: true
      isSuccess: false
      isError: false
      creditCardsRows: null
    }
  | {
      creditCardsData: null
      isCreditCardsLoading: boolean
      isSuccess: false
      isError: boolean
      creditCardsRows: null
    }

export const useCreditCardsWithStats = ({
  filters,
  paginationModel,
  queryCacheOptions,
  userId,
}: UseCreditCardsWithStatsProps): UseCreditCardsWithStatsHook => {
  const [creditCardsRows, setCreditCardsRows] = useState<
    ExtendedCreditCardListModel[]
  >([])

  const {
    data: creditCardsData,
    isLoading: isCreditCardsLoading,
    isSuccess: isCreditCardsSuccess,
  } = useQueryCreditCardsPaginated({
    filters: {
      search: filters.search !== '' ? filters.search : undefined,
      displayStatuses:
        filters.displayStatuses && filters.displayStatuses.length > 0
          ? filters.displayStatuses
          : undefined,
      user: userId,
      type: CreditCardType.Source,
      count: paginationModel.pageSize,
      page: paginationModel.page,
      sortField: 'userPendingActiveFirst',
    },
    queryCacheOptions,
  })

  const creditCardIds = creditCardsData?.creditCards.map((cc) => cc.id) || []

  const { data: statisticsBudgetsData, isSuccess: isStatisticsBudgetsSuccess } =
    useQueryCreditCardBudgetsStatistics(
      creditCardIds,
      StatisticModelType.CreditCard,
      isCreditCardsSuccess,
    )

  const { data: statisticsVcnsData, isSuccess: isStatisticsVcnsSuccess } =
    useQueryCreditCardVCNStatistics(
      creditCardIds,
      StatisticModelType.VirtualCard,
      isCreditCardsSuccess,
    )

  const addStatsToRows = (): ExtendedCreditCardListModel[] => {
    if (
      isStatisticsBudgetsSuccess &&
      statisticsBudgetsData &&
      creditCardsData &&
      isStatisticsVcnsSuccess &&
      statisticsVcnsData
    ) {
      return creditCardsData.creditCards.map((card: CreditCardListModel) => {
        const extendedCard: ExtendedCreditCardListModel = Object.assign(
          {},
          card,
          {
            numBudgets: statisticsBudgetsData.statistics[card.id] || 0,
            numVcns: statisticsVcnsData.statistics[card.id] || 0,
          },
        )
        return extendedCard
      })
    }
    return []
  }

  useEffect(() => {
    const successfullyLoadedStatsAndCCs = !!(
      isStatisticsBudgetsSuccess &&
      statisticsBudgetsData &&
      creditCardsData &&
      isStatisticsVcnsSuccess &&
      statisticsVcnsData
    )

    if (successfullyLoadedStatsAndCCs) {
      const newRows = addStatsToRows()
      if (!_.isEqual(newRows, creditCardsRows)) {
        setCreditCardsRows(newRows)
      }
    } else if (creditCardsData && isCreditCardsSuccess) {
      if (!_.isEqual(creditCardsData.creditCards, creditCardsRows)) {
        setCreditCardsRows(creditCardsData.creditCards)
      }
    }
  }, [
    isStatisticsBudgetsSuccess,
    isStatisticsVcnsSuccess,
    isCreditCardsSuccess,
    filters,
    paginationModel,
  ])

  const successfullyLoadedCCs = !!(isCreditCardsSuccess && creditCardsData)
  if (successfullyLoadedCCs) {
    return {
      creditCardsData,
      isCreditCardsLoading: false,
      isSuccess: isCreditCardsSuccess,
      isError: false,
      creditCardsRows,
    }
  }

  return {
    creditCardsData: null,
    isCreditCardsLoading,
    isSuccess: false,
    isError: true,
    creditCardsRows: null,
  }
}
